import { useState, useEffect, useCallback, useMemo } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Save, X, Plus, FileText, Pencil, Eye, MoreVertical, Clock, Trash2, AlertTriangle } from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { MarkdownText } from '@/components/chat/MarkdownText';
import { Switch } from '@/components/ui/switch';
import { MAX_ARTICLE_BODY_CHARS, type KnowledgeBaseItem } from '@/services/knowledgeBase.service';
import { formatShortDate, formatDateTime } from '@/utils/formatDate';

interface ArticleEditorProps {
  article: KnowledgeBaseItem;
  pendingUpdateArticle?: KnowledgeBaseItem;
  onSave: (updates: Partial<KnowledgeBaseItem>) => void;
  saving?: boolean;
  readOnly?: boolean;
  initialEditing?: boolean;
  onMarkPending?: () => void;
  onDelete?: () => void;
}

export default function ArticleEditor({ article, pendingUpdateArticle, onSave, saving, readOnly, initialEditing, onMarkPending, onDelete }: ArticleEditorProps) {
  const [viewingUpdate, setViewingUpdate] = useState(!!pendingUpdateArticle);
  const [name, setName] = useState(article.name);
  const [body, setBody] = useState(article.body || '');
  const [tags, setTags] = useState<string[]>(article.tags || []);
  const [tagInput, setTagInput] = useState('');
  const [dirty, setDirty] = useState(false);
  const [editing, setEditing] = useState(initialEditing && !readOnly);
  const [pendingEditPromptOpen, setPendingEditPromptOpen] = useState(false);

  const activeArticle = useMemo(() => {
    if (viewingUpdate && pendingUpdateArticle) return pendingUpdateArticle;
    return article;
  }, [viewingUpdate, pendingUpdateArticle, article]);

  const syncFromArticle = useCallback((a: KnowledgeBaseItem) => {
    setName(a.name);
    setBody(a.body || '');
    setTags(a.tags || []);
    setTagInput('');
    setDirty(false);
    setEditing(false);
  }, []);

  useEffect(() => {
    const initial = pendingUpdateArticle ?? article;
    syncFromArticle(initial);
    setViewingUpdate(!!pendingUpdateArticle);
    setEditing(initialEditing && !readOnly);
  }, [article.id]);

  useEffect(() => {
    syncFromArticle(activeArticle);
  }, [viewingUpdate]);

  const markDirty = useCallback(() => setDirty(true), []);

  const overLimit = body.length > MAX_ARTICLE_BODY_CHARS;

  const handleSave = () => {
    onSave({
      id: activeArticle.id,
      name: name.trim() || activeArticle.name,
      body,
      tags,
    });
    setDirty(false);
  };

  const addTag = (raw: string) => {
    const tag = raw.trim().toLowerCase();
    if (tag && !tags.includes(tag)) {
      setTags(prev => [...prev, tag]);
      markDirty();
    }
    setTagInput('');
  };

  const removeTag = (tag: string) => {
    setTags(prev => prev.filter(t => t !== tag));
    markDirty();
  };

  const handleTagKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' || e.key === ',') {
      e.preventDefault();
      addTag(tagInput);
    }
    if (e.key === 'Backspace' && tagInput === '' && tags.length > 0) {
      removeTag(tags[tags.length - 1]);
    }
  };

  const handleEditClick = () => {
    if (editing) {
      setEditing(false);
      return;
    }
    if (!viewingUpdate && pendingUpdateArticle) {
      setPendingEditPromptOpen(true);
      return;
    }
    setEditing(true);
  };

  const handleToggleVersion = (checked: boolean) => {
    setViewingUpdate(checked);
  };

  const createdLabel = formatShortDate(activeArticle.created_at);
  const updatedLabel = formatShortDate(activeArticle.last_updated || activeArticle.created_at);

  return (
    <div className="flex flex-col h-full">
      <div className="flex items-center gap-2 px-4 py-3 border-b">
        <FileText className="h-4 w-4 text-muted-foreground shrink-0" />
        <Input
          value={name}
          onChange={e => { setName(e.target.value); markDirty(); }}
          className="text-lg font-semibold border-none shadow-none px-0 h-auto focus-visible:ring-0 hover:underline"
          placeholder="Article title"
          readOnly={readOnly}
        />
        {!readOnly && (
          <>
            {pendingUpdateArticle && (
              <div className="flex items-center gap-2 shrink-0">
                <span className="text-xs text-muted-foreground">View update</span>
                <Switch
                  checked={viewingUpdate}
                  onCheckedChange={handleToggleVersion}
                />
              </div>
            )}
            <Button
              size="sm"
              variant="outline"
              onClick={handleEditClick}
            >
              {editing ? <><Eye className="h-4 w-4 mr-1" />Preview</> : <><Pencil className="h-4 w-4 mr-1" />Edit</>}
            </Button>
            <Button
              size="sm"
              onClick={handleSave}
              disabled={saving || !dirty || overLimit}
            >
              <Save className="h-4 w-4 mr-1" />
              {saving ? 'Saving...' : 'Save'}
            </Button>
            {article.status === 'active' && (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button size="sm" variant="ghost" disabled={viewingUpdate}>
                    <MoreVertical className="h-4 w-4" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuItem onClick={onMarkPending}>
                    <Clock className="h-4 w-4 mr-2" />
                    Mark as Pending
                  </DropdownMenuItem>
                  <DropdownMenuItem className="text-destructive" onClick={onDelete}>
                    <Trash2 className="h-4 w-4 mr-2" />
                    Delete
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            )}
          </>
        )}
      </div>

      {(createdLabel || updatedLabel) && (
        <div className="flex items-center gap-3 px-4 py-1.5 border-b text-xs text-muted-foreground">
          {createdLabel && (
            <span title={formatDateTime(activeArticle.created_at) ?? undefined}>
              Created {createdLabel}
            </span>
          )}
          {updatedLabel && (
            <span title={formatDateTime(activeArticle.last_updated || activeArticle.created_at) ?? undefined}>
              Updated {updatedLabel}
            </span>
          )}
        </div>
      )}

      {overLimit && (
        <div className="flex items-center gap-2 px-4 py-2 border-b bg-destructive/10 text-destructive text-sm">
          <AlertTriangle className="h-4 w-4 shrink-0" />
          <span>
            This article is {body.length.toLocaleString()} characters — over the{' '}
            {MAX_ARTICLE_BODY_CHARS.toLocaleString()} character limit. Long articles overload
            Ellie's context, so it can't be saved. Shorten it or split it into multiple
            focused articles.
          </span>
        </div>
      )}

      <div className="px-4 py-2 border-b">
        <div className="flex items-center gap-1.5 mb-1.5">
          <span className="text-xs font-bold">Tags</span>
          <span className="text-xs text-muted-foreground">Tags help Ellie find relevant information</span>
        </div>
        <div className="flex flex-wrap items-center gap-1.5">
          {tags.map(tag => (
            <Badge key={tag} variant="secondary" className="gap-1">
              {tag}
              {!readOnly && (
                <X
                  className="h-3 w-3 cursor-pointer hover:text-destructive"
                  onClick={() => removeTag(tag)}
                />
              )}
            </Badge>
          ))}
          {!readOnly && (
            <div className="flex items-center gap-1">
              <Plus className="h-3.5 w-3.5 text-muted-foreground" />
              <Input
                value={tagInput}
                onChange={e => setTagInput(e.target.value)}
                onKeyDown={handleTagKeyDown}
                onBlur={() => { if (tagInput.trim()) addTag(tagInput); }}
                placeholder="Add tag..."
                className="h-6 w-24 text-xs border-none shadow-none px-0 focus-visible:ring-0"
              />
            </div>
          )}
        </div>
      </div>

      <div className="flex-1 min-h-0 overflow-auto px-8 py-6">
        {editing ? (
          <Textarea
            value={body}
            onChange={e => { setBody(e.target.value); markDirty(); }}
            placeholder="Write your article in markdown..."
            className="h-full min-h-[200px] resize-none font-mono text-sm"
          />
        ) : body ? (
          <MarkdownText content={body} />
        ) : (
          <p className="text-muted-foreground italic">Nothing to preview</p>
        )}
      </div>

      <AlertDialog open={pendingEditPromptOpen} onOpenChange={setPendingEditPromptOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Pending update available</AlertDialogTitle>
            <AlertDialogDescription>
              Ellie has proposed an update to this article. Would you like to review the proposed changes before editing the original?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => {
              setPendingEditPromptOpen(false);
              setEditing(true);
            }}>
              Edit original
            </AlertDialogCancel>
            <AlertDialogAction onClick={() => {
              setPendingEditPromptOpen(false);
              setViewingUpdate(true);
            }}>
              View proposed update
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
